import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Send, Bot, Calculator, Euro, TrendingUp, TrendingDown,
  FileText, Calendar, RefreshCw, Loader2, MapPin,
  PieChart, BarChart3, Download, Map, Building2,
  AlertTriangle, CheckCircle, Lightbulb, Eye, Volume2, Settings,
  FolderOpen
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { SpeakButtonCompact } from '../components/SpeakButton';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, PieChart as RechartsPie, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, AreaChart, Area
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Thèmes de l'assistant comptable
const THEMES_COMPTA = [
  { id: 'situation', label: 'Situation mensuelle', icon: PieChart, color: 'bg-blue-600', description: 'Bilan du mois en cours' },
  { id: 'tva', label: 'Préparation TVA', icon: Euro, color: 'bg-amber-500', description: 'CA12, RSA, RF' },
  { id: 'fiscal', label: 'Simulation fiscale', icon: Building2, color: 'bg-indigo-500', description: 'Micro-BA vs Réel' },
  { id: 'cloture', label: 'Checklist clôture', icon: CheckCircle, color: 'bg-green-500', description: 'Pré-clôture exercice' },
  { id: 'msa', label: 'Cotisations MSA', icon: Calendar, color: 'bg-red-500', description: 'Appels, prévisions' },
  { id: 'tresorerie', label: 'Trésorerie', icon: TrendingUp, color: 'bg-cyan-500', description: 'Soldes, prévisions' },
  { id: 'charges', label: 'Charges', icon: TrendingDown, color: 'bg-red-500', description: 'Analyse des dépenses' },
  { id: 'export', label: 'Exports', icon: Download, color: 'bg-purple-500', description: 'FEC, CSV, liasse' },
];

// Suggestions rapides
const SUGGESTIONS_RAPIDES = [
  "Situation comptable du mois",
  "Préparer ma déclaration TVA",
  "Simulation résultat fiscal micro-BA vs réel",
  "Pièces manquantes et alertes",
  "Cotisations MSA prévisionnelles",
  "Checklist pré-clôture",
  "Répartition charges par catégorie",
  "Factures impayées à relancer",
  "Comparaison N-1"
];

// Couleurs pour les graphiques
const COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function AssistantComptablePage() {
  const { activeSociete } = useWorkspace();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const messagesEndRef = useRef(null);

  const fetchAlertes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/comptabilite/alertes-proactives`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-Societe-Id': activeSociete?.id || '' }
      });
      if (res.ok) {
        const data = await res.json();
        setAlertes(data.alertes || []);
      }
    } catch (e) { console.error('Erreur alertes:', e); }
  };

  useEffect(() => { fetchAlertes(); }, [activeSociete]);

  // Message d'accueil
  useEffect(() => {
    const welcomeMessage = {
      id: 'welcome',
      role: 'assistant',
      content: `💼 **Assistant Comptable activé !**

Je suis votre expert comptable IA. Je peux vous aider avec :

• **Zone FRR/ZRR** - Analyser votre CA en zone rurale, avantages fiscaux
• **Chiffre d'affaires** - Évolution, graphiques, comparaisons
• **Trésorerie** - Soldes, prévisions de trésorerie
• **Charges** - Analyse et répartition des dépenses
• **Exports** - Générer FEC, CSV, exports logiciels comptables
• **Fiscal** - TVA, IS, obligations déclaratives

**Demandez-moi un graphique ou une carte** et je vous les génère instantanément !

Exemples : "Graphique CA par mois", "Carte clients FRR", "Répartition charges"`,
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (content) => {
    if (!content.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await api.post('/brain/chat', {
        message: content,
        agent_type: 'comptable',
        conversation_id: conversationId
      });

      const data = response.data;
      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        suggestions: data.suggestions,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion', { description: error?.response?.data?.detail || error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };

  const selectTheme = (theme) => {
    setSelectedTheme(theme.id);
    sendMessage(`Analyse ${theme.label} pour ma société`);
  };

  const handleSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleAction = async (action) => {
    if (action.type === 'export') {
      toast.info('Génération de l\'export en cours...');
      // Déclencher l'export via l'API
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/comptabilite/export/generer`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Societe-Id': activeSociete?.id || ''
          },
          body: JSON.stringify(action.params)
        });
        
        if (res.ok) {
          const contentType = res.headers.get('content-type');
          if (contentType && (contentType.includes('text/plain') || contentType.includes('text/csv'))) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = action.params.filename || 'export.txt';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            toast.success('Export téléchargé !');
          }
        }
      } catch (error) {
        toast.error('Erreur lors de l\'export');
      }
    }
  };

  const renderChart = (chart) => {
    if (!chart || !chart.data) return null;

    const { type, data, title } = chart;

    return (
      <div className="mt-4 p-4 bg-white rounded-lg border">
        {title && <h4 className="font-medium text-gray-700 mb-4">{title}</h4>}
        <ResponsiveContainer width="100%" height={250}>
          {type === 'pie' && (
            <RechartsPie>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </RechartsPie>
          )}
          
          {type === 'bar' && (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
              <Legend />
              <Bar dataKey="value" fill="#10B981" name="Montant" />
              {data[0]?.frr !== undefined && (
                <Bar dataKey="frr" fill="#6366F1" name="FRR" />
              )}
            </BarChart>
          )}
          
          {type === 'line' && (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} name="CA" />
              {data[0]?.frr !== undefined && (
                <Line type="monotone" dataKey="frr" stroke="#6366F1" strokeWidth={2} name="CA FRR" />
              )}
            </LineChart>
          )}
          
          {type === 'area' && (
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
              <Area type="monotone" dataKey="value" stroke="#10B981" fill="#10B98133" name="CA" />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  };

  const renderMap = (mapData) => {
    if (!mapData || !mapData.points) return null;

    // Carte simplifiée avec les points
    return (
      <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border">
        <h4 className="font-medium text-gray-700 mb-4 flex items-center gap-2">
          <Map className="w-4 h-4" /> {mapData.title || 'Carte des clients'}
        </h4>
        <div className="relative w-full h-64 bg-white rounded-lg border overflow-hidden">
          {/* Fond de carte France simplifié */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg viewBox="0 0 400 400" className="w-full h-full opacity-20">
              <path d="M180,50 Q280,80 300,150 Q320,220 280,280 Q240,340 180,350 Q120,340 80,280 Q40,220 60,150 Q80,80 180,50" 
                    fill="#10B981" stroke="#059669" strokeWidth="2"/>
            </svg>
          </div>
          
          {/* Points des clients */}
          <div className="absolute inset-0 p-4">
            <div className="grid grid-cols-3 gap-2 h-full">
              {mapData.points.slice(0, 9).map((point, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-center rounded-lg ${point.is_frr ? 'bg-green-100' : 'bg-gray-100'}`}
                  title={`${point.nom} - ${point.commune}`}
                >
                  <div className="text-center p-2">
                    <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${point.is_frr ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className="text-xs truncate block">{point.nom?.substring(0, 10)}</span>
                    {point.is_frr && <Badge className="bg-green-500 text-[10px] px-1">FRR</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Légende */}
        <div className="flex items-center gap-4 mt-3 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Zone FRR ({mapData.stats?.frr || 0})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            <span>Hors FRR ({mapData.stats?.hors_frr || 0})</span>
          </div>
        </div>
      </div>
    );
  };

  const clearChat = () => {
    setMessages([{
      id: 'welcome-reset',
      role: 'assistant',
      content: '💼 Conversation réinitialisée. Comment puis-je vous aider avec votre comptabilité ?',
      timestamp: new Date().toISOString()
    }]);
    setSelectedTheme(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-testid="assistant-comptable-page">
      <div className="container mx-auto p-4 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center">
                <Calculator className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Pierre Moreau - Comptable Agricole IA</h1>
                <p className="text-slate-600 text-sm">Expert-comptable agricole opérationnel - TVA, MSA, PAC, fiscalité</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={async () => {
                  try {
                    const tk = localStorage.getItem('token');
                    const r = await fetch(`${API_URL}/api/comptabilite/drive-scan-comptable`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${tk}`, 'X-Societe-Id': activeSociete?.id || '' }
                    });
                    if (r.ok) toast.success('Scan du Drive lancé');
                  } catch(e) { toast.error('Erreur'); }
                }}
                data-testid="btn-scan-drive"
              >
                <FolderOpen className="w-4 h-4" /> Scan Drive
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50"
                onClick={async () => {
                  try {
                    const tk = localStorage.getItem('token');
                    const r = await fetch(`${API_URL}/api/comptabilite/rapport-mensuel`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${tk}`, 'X-Societe-Id': activeSociete?.id || '' }
                    });
                    if (r.ok) toast.success('Rapport mensuel en cours...');
                  } catch(e) { toast.error('Erreur'); }
                }}
                data-testid="btn-rapport-mensuel"
              >
                <FileText className="w-4 h-4" /> Rapport
              </Button>
              <Button 
                onClick={() => navigate('/agents/config', { state: { selectedAgent: 'comptable' } })} 
                variant="outline" 
                size="sm"
                className="text-gray-600"
                data-testid="agent-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" /> Paramètres
              </Button>
              <Button onClick={clearChat} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" /> Nouvelle conversation
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar Thèmes */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-indigo-500" />
                  Thèmes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {THEMES_COMPTA.map((theme) => {
                  const Icon = theme.icon;
                  const isSelected = selectedTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => selectTheme(theme)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition ${
                        isSelected 
                          ? 'bg-indigo-100 text-indigo-900 ring-2 ring-indigo-400' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-6 h-6 ${theme.color} rounded flex items-center justify-center`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span className="truncate">{theme.label}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Alertes proactives */}
            {alertes.length > 0 && (
              <Card className="bg-white/80 backdrop-blur border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Alertes ({alertes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-60 overflow-y-auto">
                  {alertes.map((alerte, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(alerte.titre + " - " + alerte.detail)}
                      className={`w-full text-left p-2 rounded-lg border text-xs transition hover:shadow-sm ${
                        alerte.niveau === 'haute' ? 'bg-red-50 border-red-200' :
                        alerte.niveau === 'moyenne' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="font-medium truncate">{alerte.titre}</div>
                      <div className="text-muted-foreground mt-0.5 line-clamp-2">{alerte.detail}</div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Suggestions rapides */}
            <Card className="bg-white/80 backdrop-blur">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-500" />
                  Actions rapides
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {SUGGESTIONS_RAPIDES.map((suggestion, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="cursor-pointer hover:bg-indigo-50 transition"
                    onClick={() => handleSuggestion(suggestion)}
                  >
                    {suggestion}
                  </Badge>
                ))}
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
              <CardContent className="pt-6">
                <PieChart className="w-8 h-8 mb-2 opacity-80" />
                <h3 className="font-bold mb-1">Graphiques & Cartes</h3>
                <p className="text-sm text-indigo-100">
                  Demandez "graphique" ou "carte" dans votre message pour une visualisation instantanée.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Zone de chat principale */}
          <div className="lg:col-span-3">
            <Card className="bg-white/80 backdrop-blur h-[calc(100vh-200px)] flex flex-col">
              <CardContent className="flex-1 overflow-hidden p-0">
                {/* Messages */}
                <div className="h-full overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl p-4 ${
                          message.role === 'user'
                            ? 'bg-indigo-500 text-white'
                            : 'bg-white border shadow-sm'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center">
                                <Calculator className="w-4 h-4 text-indigo-600" />
                              </div>
                              <span className="text-xs text-gray-500">Assistant Comptable</span>
                            </div>
                            <SpeakButtonCompact text={message.content} voice="alloy" />
                          </div>
                        )}
                        <div className="whitespace-pre-wrap text-sm">
                          {message.content}
                        </div>
                        
                        {/* Graphique */}
                        {message.chart && renderChart(message.chart)}
                        
                        {/* Carte */}
                        {message.map && renderMap(message.map)}
                        
                        {/* Actions */}
                        {message.actions && message.actions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.actions.map((action, idx) => (
                              <Button
                                key={idx}
                                size="sm"
                                variant="outline"
                                onClick={() => handleAction(action)}
                              >
                                {action.icon === 'download' && <Download className="w-3 h-3 mr-1" />}
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                        
                        {/* Suggestions */}
                        {message.suggestions && message.suggestions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.suggestions.map((suggestion, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="cursor-pointer hover:bg-indigo-100"
                                onClick={() => handleSuggestion(suggestion)}
                              >
                                {suggestion}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                          <span className="text-sm text-gray-500">Analyse en cours...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t p-4 bg-gray-50 mb-20 md:mb-0">
                  <div className="flex gap-2">
                    <Textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Demandez une analyse, un graphique, une carte, un export..."
                      className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                      rows={1}
                    />
                    <Button
                      onClick={() => sendMessage(inputMessage)}
                      disabled={loading || !inputMessage.trim()}
                      className="bg-indigo-500 hover:bg-indigo-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
